import QuillDelta from 'quill-delta';
import { ComponentProps, ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { QuillInput } from '../form/controls/QuillInput';
import { QuillText } from '../form/controls/QuillText';
import { PlainInput } from './PlainInput';
import { WorkingArea } from './WorkingArea';
import { Button } from './button/Button';
import { IconButton } from './button/IconButton';

const Editor = styled(Box)`
	background: white;
	border-radius: 5px;
`;

export function Editable(props: {
	active?: boolean;
	flexible?: boolean;
	value?: string;
	onChange: (value: string) => Promise<void> | void;
	onDone?: () => void;
	children: (value: string | ReactNode | undefined, options: { active: boolean }) => ReactNode;
}) {
	const [optimistic, setOptimistic] = useState<string | undefined>();

	return (
		<WorkingArea
			flexible={props.flexible}
			active={props.active}
			onCancel={() => {
				setOptimistic(undefined);
				props.onDone?.();
			}}
			onSave={async () => {
				await props.onChange(optimistic ?? props.value ?? '');
				setOptimistic(undefined);
				props.onDone?.();
			}}
		>
			{(active) =>
				active ? (
					<Editor p={2} ml={-2} mt={-2} mr={-2}>
						{props.children(
							<PlainInput
								autoFocus
								selectOnFocus
								value={optimistic ?? props.value}
								onChange={setOptimistic}
							/>,
							{ active },
						)}
					</Editor>
				) : (
					props.children(optimistic ?? props.value, { active })
				)
			}
		</WorkingArea>
	);
}

function EditableQuill(props: {
	active?: boolean;
	flexible?: boolean;
	hasMediaUpload?: boolean;
	value?: QuillDelta;
	onChange: (value: QuillDelta) => Promise<void> | void;
	onDone?: () => void;
	children: (value: ReactNode | undefined, options: { active: boolean }) => ReactNode;
}) {
	const [optimistic, setOptimistic] = useState<QuillDelta | undefined>();

	const onChange = useCallback(
		(value: QuillDelta) => {
			setOptimistic(value);
		},
		[setOptimistic],
	);

	return (
		<WorkingArea
			flexible={props.flexible}
			active={props.active}
			onCancel={() => {
				setOptimistic(undefined);
				props.onDone?.();
			}}
			onSave={async () => {
				await props.onChange((optimistic ?? props.value ?? { ops: [] }) as QuillDelta);
				setOptimistic(undefined);
				props.onDone?.();
			}}
		>
			{(active) =>
				active ? (
					<Editor p={2} ml={-2} mt={-2} mr={-2}>
						{props.children(
							<QuillInput
								hasMediaUpload={props.hasMediaUpload}
								autoFocus
								value={optimistic ?? props.value}
								onChange={onChange}
							/>,
							{ active },
						)}
					</Editor>
				) : (
					props.children(
						QuillText.isEmpty(optimistic ?? props.value) ? undefined : (
							<QuillText value={optimistic ?? props.value} />
						),
						{ active },
					)
				)
			}
		</WorkingArea>
	);
}

Editable.Quill = EditableQuill;

function EditableFlex(
	props: ComponentProps<typeof Editable> & { small?: boolean; buttons?: ReactNode },
) {
	const { t } = useTranslation();

	const [active, setActive] = useState(props.active ?? false);

	return (
		<Flex alignItems="center">
			<Box flexGrow={1} flexShrink={1}>
				<Editable {...props} active={active} onDone={() => setActive(false)} />
			</Box>
			{!active && (
				<Button
					type="secondary"
					title={t('common.edit')}
					ml={3}
					small={props.small}
					onClick={() => setActive(true)}
					flexGrow={0}
					flexShrink={0}
				/>
			)}
			{props.buttons}
		</Flex>
	);
}

Editable.Flex = EditableFlex;

function EditableButton(
	props: Omit<ComponentProps<typeof Editable>, 'children'> & {
		children: (
			value: string | ReactNode | undefined,
			options: { active: boolean; button: ReactNode },
		) => ReactNode;
	},
) {
	const { t } = useTranslation();

	const [active, setActive] = useState(false);

	return (
		<Flex alignItems="center">
			<Box flexGrow={1} flexShrink={1}>
				<Editable {...props} active={active} onDone={() => setActive(false)}>
					{(value, state) =>
						props.children(value, {
							...state,
							button: (
								<Box
									style={{
										visibility: active ? 'hidden' : 'visible',
									}}
									ml={3}
									flexGrow={0}
									flexShrink={0}
								>
									<Button
										type="transparent"
										title={t('common.edit')}
										small
										onClick={() => setActive(true)}
									/>
								</Box>
							),
						})
					}
				</Editable>
			</Box>
		</Flex>
	);
}

Editable.Button = EditableButton;

function EditableQuillButton({
	buttonProps,
	children,
	...rest
}: Omit<ComponentProps<typeof EditableQuill>, 'children'> & {
	buttonProps?: ComponentProps<typeof Button>;
	children: (
		value: string | ReactNode | undefined,
		options: { active: boolean; button: ReactNode },
	) => ReactNode;
}) {
	const { t } = useTranslation();

	const [active, setActive] = useState(false);
	const iconOnly = Boolean(buttonProps?.icon) && !buttonProps?.title;

	return (
		<Flex alignItems="center">
			<Box flexGrow={1} flexShrink={1}>
				<EditableQuill {...rest} active={active} onDone={() => setActive(false)}>
					{(value, state) =>
						children(value, {
							...state,
							button: (
								<Box
									style={{
										visibility: active ? 'hidden' : 'visible',
									}}
									ml={iconOnly ? 0 : 3}
									flexGrow={0}
									flexShrink={0}
								>
									{iconOnly ? (
										<IconButton
											type={buttonProps?.type === 'destructive' ? 'destructive' : 'secondary'}
											icon={buttonProps?.icon}
											onClick={() => setActive(true)}
											aria-label={t('common.edit')}
										/>
									) : (
										<Button
											small
											type="transparent"
											title={t('common.edit')}
											onClick={() => setActive(true)}
											{...buttonProps}
										/>
									)}
								</Box>
							),
						})
					}
				</EditableQuill>
			</Box>
		</Flex>
	);
}

EditableQuill.Button = EditableQuillButton;
